import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LookupItem } from "../models/LookupItem";
import { ViewModeItem, ViewModeItems } from "../models/ViewModeItem";
import { DisciplineAvailableSortItems, SortItem } from "../models/SortItem";
import { FacetResult } from "../models/FacetResult";
import { DisciplineSummaryItem } from "../models/disciplineSummaries/DisciplineSummaryItem";
import { SuggestItem } from '../models/SuggestItem';
import { SuggestFilter } from '../models/common/SuggestFilter';

export interface DisciplineSummariesState {
  viewMode: ViewModeItem;
  orderBy: SortItem;
  keywords: string;
  suggestions: SuggestItem[];
  selectedCategory: undefined | LookupItem;
  selectedLocations: undefined | LookupItem[];
  isLoading: boolean;
  isSuggestLoading: boolean;
  canLoadMore: boolean;
  items: DisciplineSummaryItem[];
  pageSize: number;
  page: number;
  initDone: boolean;
}

const initialState: DisciplineSummariesState = {
  viewMode: ViewModeItems[0],
  orderBy: DisciplineAvailableSortItems[1],
  keywords: "",
  suggestions: [],
  selectedCategory: undefined,
  selectedLocations: undefined,
  isLoading: false,
  isSuggestLoading: false,
  canLoadMore: false,
  items: [],
  pageSize: 9,
  page: 1,
  initDone: false,
};

export type SearchFilter = {
  keywords?: string;
  category?: string;
  locations?: string[];
  sortBy?: string;
  sortAsc?: boolean;
  path?: string;
  page: number;
  pageSize: number;
};

export const searchDisciplineSummariesSuggest = createAsyncThunk<SuggestItem[], SuggestFilter>(
  "data/searchDisciplineSummariesSuggest",
  async (payload: SuggestFilter): Promise<SuggestItem[]> => {
    const response = await fetch("/disciplinesummariessearch/suggest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as SuggestItem[];
  }
);

export const searchDisciplineSummaries = createAsyncThunk<FacetResult<DisciplineSummaryItem>, SearchFilter>(
  "data/searchDisciplineSummaries",
  async (payload: SearchFilter): Promise<FacetResult<DisciplineSummaryItem>> => {
    const response = await fetch("/disciplinesummariessearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as FacetResult<DisciplineSummaryItem>;
  }
);

export const disciplineSummariesSlice = createSlice({
  name: "savingsbenefits",
  initialState,
  reducers: {
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    changeViewMode: (state, action: PayloadAction<ViewModeItem>) => {
      state.viewMode = action.payload;
    },
    changeCategory: (state, action: PayloadAction<undefined | LookupItem>) => {
      if (state.selectedCategory?.value !== action.payload?.value){
        state.items = [];
        state.canLoadMore = false;
      }

      state.selectedCategory = action.payload;
      state.page = 1;
    },
    changeLocations: (
      state,
      action: PayloadAction<undefined | LookupItem[]>
    ) => {
      state.items = [];
      state.selectedLocations = action.payload;
      state.page = 1;
      state.canLoadMore = false;
    },
    changeOrder: (state, action: PayloadAction<undefined | SortItem>) => {
      if (state.orderBy?.Title !== action.payload?.Title) {
        state.items = [];
        state.canLoadMore = false;
      }
      state.orderBy = action.payload ?? DisciplineAvailableSortItems[1];
      state.page = 1;
    },
    changeKeywords: (state, action: PayloadAction<string>) => {
      if (state.keywords !== action.payload) {
        state.page = 1;
        state.canLoadMore = false;
        state.items = [];
      }
      state.keywords = action.payload;
    },
    clearFilters: (state, action: PayloadAction<undefined | LookupItem[]>) => {
      state.selectedCategory = undefined;
      state.selectedLocations = action.payload;
      state.items = [];
      state.page = 1;
      state.canLoadMore = false;
    },
    changeInit: (state, action: PayloadAction<boolean>) => {
      state.initDone = action.payload;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchDisciplineSummaries.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        searchDisciplineSummaries.fulfilled,
        (state, action: PayloadAction<FacetResult<DisciplineSummaryItem>>) => {
          state.isLoading = false;

          if (action.payload) {
            if (state.page == 1) {
              state.items = [];
            }
            if (action.payload.hits.length > 0) {
              state.items.push(...action.payload.hits);
            }

            state.canLoadMore =
              action.payload.totalHits > state.items.length &&
              state.items.length > 0;
          }
        }
      )
      .addCase(searchDisciplineSummaries.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(searchDisciplineSummariesSuggest.pending, (state) => {
        state.isSuggestLoading = true;
      })
      .addCase(
        searchDisciplineSummariesSuggest.fulfilled,
        (state, action: PayloadAction<SuggestItem[]>) => {
          state.isSuggestLoading = false;

          state.suggestions = [];

          if (action.payload.length > 0) {
            state.suggestions = [...action.payload];
          }
        }
      )
      .addCase(searchDisciplineSummariesSuggest.rejected, (state) => {
        state.isSuggestLoading = false;
      });
  },
});

export const {
  changeCategory,
  changeLocations,
  changeKeywords,
  changePage,
  changeViewMode,
  changeOrder,
  clearFilters,
  changeInit,
  clearSuggestions
} = disciplineSummariesSlice.actions;

export default disciplineSummariesSlice.reducer;
